import React, { useState, useEffect, useCallback } from 'react';
import { getApiClient } from '../../api/apiClient';
import { 
  XMarkIcon,
  UserGroupIcon,
  PlusIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import AddEmployeeModal from './AddEmployeeModal.jsx';

const EmployeeManagementModal = ({ isOpen, onClose, shop }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);

  const fetchEmployees = useCallback(async () => {
    if (!shop?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const client = getApiClient();
      const response = await client.get(`/admin/shop/list-employees/${shop.id}`);
      
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      if (error.response?.status === 403) {
        setError('Insufficient permissions to view employees');
      } else {
        setError('Failed to load employees');
      }
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [shop?.id]);

  // Fetch employees when modal opens
  useEffect(() => {
    if (isOpen && shop?.id) {
      fetchEmployees();
    }
  }, [isOpen, shop?.id, fetchEmployees]);

  const handleAddEmployeeSuccess = () => {
    setShowAddEmployeeModal(false);
    fetchEmployees(); // Refresh the employee list
  };

  const formatWorkingHours = (workingHours) => {
    if (!workingHours || workingHours.length === 0) {
      return 'No schedule set';
    }

    const daysMap = {
      0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat'
    };

    return workingHours
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
      .map(wh => `${daysMap[wh.dayOfWeek]} ${wh.startTime}-${wh.endTime}`)
      .join(', ');
  };

  const getSpecializationBadgeColor = (specialization) => {
    const colors = {
      'MECHANIC': 'bg-blue-100 text-blue-800',
      'ELECTRICIAN': 'bg-yellow-100 text-yellow-800',
      'BODYWORK': 'bg-green-100 text-green-800',
      'DIAGNOSTICS': 'bg-purple-100 text-purple-800',
      'MANAGER': 'bg-red-100 text-red-800',
      'APPRENTICE': 'bg-gray-100 text-gray-800'
    };
    return colors[specialization] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-5 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-3/4 max-w-5xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-6 w-6 text-purple-600 mr-2" />
              <h3 className="text-xl font-semibold text-gray-900">
                Employee Management
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Shop Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Shop Information</h4>
            <div className="text-sm text-blue-700">
              <p><strong>Brand Name:</strong> {shop?.name}</p>
              <p><strong>Email:</strong> {shop?.email}</p>
            </div>
          </div>

          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-semibold text-gray-800">
              Employees ({employees.length})
            </h4>
            <button
              onClick={() => setShowAddEmployeeModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Employee
            </button>
          </div>

          {/* Employee List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading employees...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Employees</h3>
              <p className="mt-1 text-sm text-red-600">{error}</p>
              <div className="mt-6">
                <button
                  onClick={fetchEmployees}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Employees</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first employee.</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowAddEmployeeModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add First Employee
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {employees.map((employee) => (
                <div key={employee.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="bg-purple-100 rounded-full p-2 mr-3">
                        <UserIcon className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h5 className="text-lg font-medium text-gray-900">{employee.name}</h5>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSpecializationBadgeColor(employee.specialization)}`}>
                          {employee.specialization}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit Employee"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Employee"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{employee.email}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{employee.phone}</span>
                    </div>
                    
                    <div className="flex items-start text-sm text-gray-600">
                      <ClockIcon className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                      <span className="break-all">{formatWorkingHours(employee.workingHours)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add Employee Modal */}
      <AddEmployeeModal 
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onSuccess={handleAddEmployeeSuccess}
        shop={shop}
      />
    </>
  );
};

export default EmployeeManagementModal;